import { Flex, Text, Box, Link } from '@chakra-ui/react';

import DetailCard from '../shared/DetailCard';
import DetailRow from '../shared/DetailRow';
import AddressValue from '../shared/AddressValue';

interface IncomingDetailsProps {
  senderAccountId: string;
  transferType: 'hbar' | 'token' | 'nft' | 'airdrop';
  formattedAmount: string;
  tokenSymbol?: string;
  tokenId?: string;
  tokenName?: string;
  nftSerialNumber?: number;
  txId?: string;
  timestamp: Date;
  hashScanUrl: string | null;
  onCopy: (text: string) => void;
  onSaveAddress: (address: string) => void;
  onTokenClick?: () => void;
}

const formatTxIdForDisplay = (txId: string): string => {
  // Convert 0.0.5809798-1772460310-207892820 → 0.0.5809798@1772460310.207892820
  const match = txId.match(/^(\d+\.\d+\.\d+)-(\d+)-(\d+)$/);
  if (match) {
    const [, acct, seconds, nanos] = match;
    return `${acct}@${seconds}.${nanos}`;
  }
  return txId;
};

const IncomingDetails = ({
  senderAccountId,
  transferType,
  formattedAmount,
  tokenSymbol,
  tokenId,
  tokenName,
  nftSerialNumber,
  txId,
  timestamp,
  hashScanUrl,
  onCopy,
  onSaveAddress,
  onTokenClick,
}: IncomingDetailsProps) => {
  const renderAmountValue = () => {
    switch (transferType) {
      case 'hbar':
        return `${formattedAmount} HBAR`;
      case 'token':
      case 'airdrop':
        return `${formattedAmount} ${tokenSymbol || 'tokens'}`;
      case 'nft':
        return `${tokenName || 'Unknown token'} #${nftSerialNumber ?? '?'}`;
      default:
        return formattedAmount;
    }
  };

  const showTokenId = (transferType === 'token' || transferType === 'airdrop' || transferType === 'nft') && tokenId;

  return (
    <Flex direction="column" px={4} py={6} borderRadius={8} gap={4} bg="primary">
      <DetailCard>
        <DetailRow
          label="From"
          value={
            <AddressValue
              address={senderAccountId}
              onCopy={onCopy}
              onSave={onSaveAddress}
            />
          }
        />
        <DetailRow label={transferType === 'nft' ? 'NFT' : 'Amount'} value={renderAmountValue()} />
        {showTokenId && (
          <DetailRow
            label="Token ID"
            value={
              onTokenClick ? (
                <Text
                  size="sm"
                  variant="bodyBold"
                  color="blue.500"
                  cursor="pointer"
                  _hover={{ textDecoration: 'underline' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onTokenClick();
                  }}
                >
                  {tokenId}
                </Text>
              ) : (
                tokenId!
              )
            }
            onCopy={() => onCopy(tokenId!)}
          />
        )}
        <DetailRow
          label="Tx ID"
          value={txId ? formatTxIdForDisplay(txId) : '\u2014'}
          onCopy={txId ? () => onCopy(formatTxIdForDisplay(txId)) : undefined}
          wordBreak="break-all"
        />
        <DetailRow
          label="Time"
          value={timestamp.toLocaleString(undefined, {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            hour12: false,
            minute: '2-digit',
            second: '2-digit',
          })}
        />
      </DetailCard>

      {hashScanUrl && (
        <Flex justifyContent="flex-start" alignItems="center">
          <Link
            href={hashScanUrl}
            isExternal
            display="inline-flex"
            alignItems="center"
            gap={1.5}
            px={3}
            py={1.5}
            fontSize="xs"
            fontWeight="500"
            color="#2d8a45"
            bg="#f0faf2"
            borderRadius={6}
            _hover={{ bg: '#e5f7ea', color: '#1a6e32' }}
            transition="all 0.15s"
          >
            View in Explorer
          </Link>
        </Flex>
      )}
    </Flex>
  );
};

export default IncomingDetails;
